import React, { useState, useEffect } from 'react';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Paper, Grid, Card, CardHeader, List, ListItemButton, ListItemIcon, Checkbox, Divider, CircularProgress } from '@mui/material';
import styled from 'styled-components'
import { useGenerateIssueActionMutation, useLazyGetIssueActionQuery } from '../../../services/minute';
import { Text } from './CreatePage';

const IssueActionWarningText = styled.p`
  font-size: 12px;
  color: red;
  margin-bottom: 0px;
  margin-top: 0px;
`

interface IssueActionTableProps {
    initialIssues: string[];
    initialActions: string[];
    memoid: string | undefined;
    content: string | null;
    onIssueActionChange: (issueActions: { issue: string; actions: { action: string }[] }[]) => void;
}

interface IssueAction {
    id: number;
    issue: string;
    action: string;
}

const IssueActionTable: React.FC<IssueActionTableProps> = ({ initialIssues, initialActions, memoid, content, onIssueActionChange }) => {
    const [issues, setIssues] = useState(initialIssues);
    const [actions, setActions] = useState(initialActions);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // 現在のTableBodyのデータをexistingDataに入れる
    const [existingData, setExistingData] = useState<IssueAction[]>([]);
    const [addData, setAddData] = useState<IssueAction[]>([]);
    const [checked, setChecked] = useState<IssueAction[]>([]);

    const [generateIssueAction] = useGenerateIssueActionMutation();
    const [getIssueAction] = useLazyGetIssueActionQuery();

    useEffect(() => {
        setIssues(initialIssues);
        setActions(initialActions);
    }, [initialIssues, initialActions]);

    useEffect(() => {
        onIssueActionChange(issues.map((issue, i) => ({
            issue: issue,
            actions: actions[i].split('\n').map(action => ({ action }))
        })));
    }, [issues]);

    useEffect(() => {
        onIssueActionChange(issues.map((issue, i) => ({
            issue: issue,
            actions: actions[i].split('\n').map(action => ({ action }))
        })));
    }, [actions]);

    const fetchDummyData = async (memoid: string, content: string | null) => {
        setLoading(true);
        setExistingData(
            issues.map((issue, index) => ({
                id: index,
                issue: issue,
                action: actions[index],
            }))
        );
        try {
            const requestBody = { memo_id: memoid, content };
            const { execute_id } = await generateIssueAction(requestBody).unwrap();
            let status = 'InProgress';
            while (status === 'InProgress') {
                await new Promise((resolve) => setTimeout(resolve, 5000));
                const result = await getIssueAction(execute_id).unwrap();
                status = result.status;
                if (status === 'Completed') {
                    const newAddData = result.issue_action.map((item, index) => ({
                        id: index + issues.length,
                        issue: item.issue,
                        action: item.actions.map(action => action.action).join('\n'),
                    }));
                    // 状態更新行う
                    setAddData(newAddData);
                    setChecked(newAddData);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRow = () => {
        setIssues([...issues, '']);
        setActions([...actions, '']);
    };

    const handleDeleteRow = (index: number) => {
        const newIssues = issues.filter((_, i) => i !== index);
        const newActions = actions.filter((_, i) => i !== index);
        setIssues(newIssues);
        setActions(newActions);
    };

    const handleIssueChange = (index: number, value: string) => {
        const newIssues = [...issues];
        newIssues[index] = value;
        setIssues(newIssues);
    };

    const handleActionChange = (index: number, value: string) => {
        const newActions = [...actions];
        newActions[index] = value;
        setActions(newActions);
    };

    const handleToggle = (value: IssueAction) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleCheckedAdd = () => {
        // handleCheckedExistingDataの処理
        const newExistingData = existingData.concat(addDataChecked);
        const newAddData = not(addData, addDataChecked);
        setExistingData(newExistingData);
        setAddData(newAddData);
        setChecked(not(checked, addDataChecked));

        // 空の行を削除
        const cleanedData = newExistingData.filter(item =>
            item.issue.trim() !== "" || item.action.trim() !== ""
        );

        // handleConfirmの処理
        setIssues(cleanedData.map(item => item.issue));
        setActions(cleanedData.map(item => item.action));
        setIsEditing(false);
    };

    const handleGenerateIssuesAndActions = async () => {
        if (!memoid) {
            return;
        }

        // 編集モードに切り替える
        setIsEditing(true);

        // 非同期処理の完了を待つ
        await fetchDummyData(memoid, content);
        setLoading(false);
    };

    // ユーティリティ関数
    function not(a: readonly IssueAction[], b: readonly IssueAction[]) {
        return a.filter((value) => !b.includes(value));
    }

    function intersection(a: readonly IssueAction[], b: readonly IssueAction[]) {
        return a.filter((value) => b.includes(value));
    }

    function union(a: readonly IssueAction[], b: readonly IssueAction[]) {
        return [...a, ...not(b, a)];
    }

    const addDataChecked = intersection(checked, addData);

    // 全選択/全解除
    const handleToggleAllAddData = () => {
        if (numberOfChecked(addData) === addData.length) {
            setChecked(not(checked, addData));
        } else {
            setChecked(union(checked, addData));
        }
    };

    // チェックされた項目の数を返す
    const numberOfChecked = (items: readonly IssueAction[]) => intersection(checked, items).length;

    const handleTextChange = (id: number, field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (existingData.some(item => item.id === id)) {
            const newExistingData = existingData.map((item) =>
                item.id === id ? { ...item, [field]: event.target.value } : item
            );
            setExistingData(newExistingData);
        } else if (addData.some(item => item.id === id)) {
            const newAddData = addData.map((item) =>
                item.id === id ? { ...item, [field]: event.target.value } : item
            );
            setAddData(newAddData);
        }
    };

    const customList = (title: React.ReactNode, items: readonly IssueAction[], handleToggleAll: () => void) => (
        <Card>
            <CardHeader
                title={title}
                action={
                    <Checkbox
                        onClick={handleToggleAll}
                        checked={numberOfChecked(items) === items.length && items.length !== 0}
                        indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
                        disableRipple
                    />
                }
            />
            <Divider />
            <List>
                {items.map((value: IssueAction) => {
                    const labelId = `transfer-list-all-item-${value.id}-label`;

                    return (
                        <ListItemButton key={value.id} role="listitem">
                            <ListItemIcon onClick={handleToggle(value)}>
                                <Checkbox
                                    checked={checked.indexOf(value) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': labelId }}
                                />
                            </ListItemIcon>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        id={`issue-${value.id}`}
                                        variant="outlined"
                                        fullWidth
                                        value={value.issue}
                                        onChange={handleTextChange(value.id, 'issue')}
                                        multiline
                                        minRows={1}
                                        InputProps={{ style: { fontSize: 12 } }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        id={`action-${value.id}`}
                                        variant="outlined"
                                        fullWidth
                                        value={value.action}
                                        onChange={handleTextChange(value.id, 'action')}
                                        multiline
                                        minRows={1}
                                        InputProps={{ style: { fontSize: 12 } }}
                                    />
                                </Grid>
                            </Grid>
                        </ListItemButton>
                    );
                })}
            </List>
        </Card>
    );

    return (
        <div>
            <Grid item style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Text style={{ display: 'flex' }}>
                        課題とアクション
                        {!isEditing && (
                            <Typography style={{ textAlign: 'left', marginTop: 'auto', marginLeft: '5px', fontSize: '1rem' }} variant="subtitle1">
                                {`: ${issues.length}`}
                            </Typography>
                        )}
                    </Text>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                        {(!memoid || !content) && <IssueActionWarningText style={{ marginLeft: '10px' }}>議事録保存後に内容を記載することで使用できます。</IssueActionWarningText>}
                        <Button variant="contained" onClick={handleGenerateIssuesAndActions} disabled={isEditing || loading || !memoid || !content}>課題とアクションの生成</Button>
                    </div>
                </div>
            </Grid>
            <TableContainer
                component={Paper}
                style={{
                    marginTop: '10px',
                    maxHeight: 300,
                    padding: '0',
                    width: '100%',
                    boxShadow: undefined,
                    pointerEvents: loading || isEditing ? 'none' : 'auto',
                    position: 'relative',
                    overflowY: 'auto' // スクロールを有効にする
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: '5%' }}></TableCell>
                            <TableCell style={{ width: '45%' }}>課題</TableCell>
                            <TableCell style={{ width: '45%' }}>アクション</TableCell>
                            <TableCell style={{ width: '5%' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {issues.length === 0 && actions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4}>課題とアクションがありません</TableCell>
                            </TableRow>
                        ) : (
                            issues.map((issue: string, index: number) => (
                                <TableRow key={index}>
                                    <TableCell style={{ borderBottom: 'none' }}>{index + 1}</TableCell>
                                    <TableCell>
                                        <TextField
                                            value={issue}
                                            onChange={(e) => handleIssueChange(index, e.target.value)}
                                            fullWidth
                                            multiline
                                            InputProps={{ style: { fontSize: '0.875rem' }, readOnly: isEditing }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            value={actions[index]}
                                            onChange={(e) => handleActionChange(index, e.target.value)}
                                            fullWidth
                                            multiline
                                            InputProps={{ style: { fontSize: '0.875rem' }, readOnly: isEditing }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button variant="contained" color="secondary" onClick={() => handleDeleteRow(index)} disabled={isEditing}>削除</Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Grid item style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', width: '100%' }}>
                    <div style={{ marginLeft: 'auto' }}>
                        {!loading && isEditing ? null : (
                            <Button style={{ marginTop: '10px', marginLeft: '10px' }} variant="contained" onClick={handleAddRow} disabled={loading}>
                                行を追加
                            </Button>
                        )}
                    </div>
                </div>
            </Grid>
            {isEditing && (
                <Paper
                    style={{
                        position: 'fixed',
                        right: '2%',
                        bottom: '2%',
                        width: '46%',
                        height: '46%',
                        padding: '10px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
                        zIndex: 1000,
                        overflowY: 'auto' // スクロールを有効にする
                    }}
                >
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <Grid container spacing={2} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Grid item xs={12}>
                                {customList('AIが作成した課題とアクション', addData, handleToggleAllAddData)}
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                                <Button variant="contained" onClick={handleCheckedAdd}>追加</Button>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setLoading(false);
                                        setIsEditing(false);
                                    }}
                                >
                                    閉じる
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </Paper>
            )}
        </div>
    );
};

export default IssueActionTable;